import { Suspense } from "react";

import Image from "next/image";
import Link from "next/link";

import {
  BookOutlined,
  HomeFilled,
  LockOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { type VariantProps, tv } from "tailwind-variants";

import { env } from "@/env.mjs";
import { cn } from "@/lib/utils";

import SignOutButton from "../organisms/navbar/sign-out-button";
import MyAccountButton from "./my-account-button";
import MyAccountDropdown from "./my-account-dropdown";

const ICON_CLASSES = "self-center text-lg";
const LABEL_CLASSES = "text-sm font-normal my-0 px-1";
const SIDEBAR_BUTTON_CLASSES = "flex flex-col gap-2";

const sidebarVariants = tv({
  base: "sticky left-0 top-0 hidden h-screen w-[76px] flex-shrink-0 flex-col items-center justify-between bg-brand-dark text-center text-dr-white-600 lg:flex",
  variants: {
    type: {
      Talento: "bg-lilac-geode-1000",
      Lead: "bg-brand-dark",
      Gestor: "bg-brand-dark",
      Representante: "bg-brand-dark",
      Hunter_Externo: "bg-brand-dark",
      Hunter_Interno: "bg-brand-dark",
      Owner: "bg-brand-dark",
      Owner_Externo: "bg-brand-dark",
      Scout: "bg-brand-dark",
    },
  },
  defaultVariants: {
    type: "Talento",
  },
});

type sidebarVariantProps = VariantProps<typeof sidebarVariants>;

export default function Sidebar({ type }: sidebarVariantProps) {
  return (
    <nav className={sidebarVariants({ type })}>
      <div>
        <div className="flex flex-col items-center py-6">
          <Image src="/logo-rounded.svg" alt="Harpio" width={52} height={52} />
        </div>
        <div className="flex flex-col gap-6 px-1">
          <Link className={SIDEBAR_BUTTON_CLASSES} href="/">
            <HomeFilled className={ICON_CLASSES} />
            <p className={LABEL_CLASSES}>Início</p>
          </Link>

          {type === "Lead" && (
            <Link className={SIDEBAR_BUTTON_CLASSES} href="/abertura-de-vagas">
              <PlusCircleOutlined className={ICON_CLASSES} />
              {/* <LockOutlined className={ICON_CLASSES} /> */}
              <p className={LABEL_CLASSES}>Nova Vaga</p>
            </Link>
          )}
          {type === "Talento" && (
            <>
              <Link className={cn(SIDEBAR_BUTTON_CLASSES)} href="/profile">
                <UserOutlined className={ICON_CLASSES} />
                <p className={LABEL_CLASSES}>Minha conta</p>
              </Link>
              <Link
                className={cn(SIDEBAR_BUTTON_CLASSES)}
                href={env.NEXT_PUBLIC_HARPIO_JOBS_URL}
              >
                <SearchOutlined className={ICON_CLASSES} />
                <p className={LABEL_CLASSES}>Novas vagas</p>
              </Link>
            </>
          )}
          {["Gestor", "Representante"].includes(String(type)) && (
            <>
              <Link className={SIDEBAR_BUTTON_CLASSES} href="/job/historico">
                <BookOutlined className={ICON_CLASSES} />
                <p className={LABEL_CLASSES}>Histórico</p>
              </Link>
              <div className={cn(SIDEBAR_BUTTON_CLASSES, "cursor-not-allowed")}>
                {/* <CommentOutlined className={ICON_CLASSES} /> */}
                <LockOutlined className={ICON_CLASSES} />
                <p className={LABEL_CLASSES}>Chat</p>
              </div>
              <div className={cn(SIDEBAR_BUTTON_CLASSES, "cursor-not-allowed")}>
                {/* <CalendarOutlined className={ICON_CLASSES} /> */}
                <LockOutlined className={ICON_CLASSES} />
                <p className={LABEL_CLASSES}>Agenda</p>
              </div>
              <Link
                className={SIDEBAR_BUTTON_CLASSES}
                href="/abertura-de-vagas"
              >
                <PlusCircleOutlined className={ICON_CLASSES} />
                {/* <LockOutlined className={ICON_CLASSES} /> */}
                <p className={LABEL_CLASSES}>Nova Vaga</p>
              </Link>
            </>
          )}
          {[
            "Owner",
            "Owner_Externo",
            "Hunter_Interno",
            "Hunter_Externo",
          ].includes(String(type)) && (
            <>
              <Link className={SIDEBAR_BUTTON_CLASSES} href="/job/historico">
                <BookOutlined className={ICON_CLASSES} />
                <p className={LABEL_CLASSES}>Histórico</p>
              </Link>
            </>
          )}
        </div>
      </div>
      <div className="flex w-full flex-col">
        {["Gestor", "Representante"].includes(String(type)) && (
          <>
            <div className="flex flex-col gap-2 py-6">
              <Suspense fallback={<MyAccountButton />}>
                <MyAccountDropdown />
              </Suspense>
            </div>
            <hr className="my-0 h-px w-full border-0 bg-white" />
          </>
        )}
        <SignOutButton />
      </div>
    </nav>
  );
}
